import React, { useState, useEffect } from 'react';
import { X, AlertTriangle, Loader2, CheckCircle2, Info } from 'lucide-react';
import { useAuth } from '../../../../contexts/AuthContext';
import { stcBinService } from '../../../../services/stcBinService';
import { CreateRackRequest, ValidateRackResponse } from './types';

interface CreateRackDialogProps {
    warehouseID: string;
    onClose: () => void;
    onSuccess: () => void;
}

type RackFormat = '01' | '1' | 'A' | 'AA';

/**
 * Dialog for creating new racks (FR-106)
 * 
 * Features:
 * - Input fields for rack number, verticals, shelves, format
 * - Default values for all locations
 * - Preview section showing example BinCode and location count
 * - Validation before creation with conflict warnings
 * - Progress indicator during creation
 */
export const CreateRackDialog: React.FC<CreateRackDialogProps> = ({
    warehouseID,
    onClose,
    onSuccess
}) => {
    const { user } = useAuth();
    const companyNo = user?.companyNo || '';

    // Form state
    const [rackNumber, setRackNumber] = useState('');
    const [verticalCount, setVerticalCount] = useState(6);
    const [shelfCount, setShelfCount] = useState(6);
    const [format, setFormat] = useState<RackFormat>('01');

    // 4D rack (drawer) settings
    const [enableDrawers, setEnableDrawers] = useState(false);
    const [drawerCount, setDrawerCount] = useState(0);

    // Default values
    const [stcZoneID, setStcZoneID] = useState('001');
    const [binSizeNo, setBinSizeNo] = useState(1);
    const [binTypeNo, setBinTypeNo] = useState(1);
    const [resourceNo, setResourceNo] = useState<number | undefined>(undefined);
    const [binGrpNo, setBinGrpNo] = useState<number | undefined>(undefined);
    const [pickPriorityNo, setPickPriorityNo] = useState<number | undefined>(50);
    const [pickableYesNo, setPickableYesNo] = useState(true);
    const [reservationAllowed, setReservationAllowed] = useState(true);
    const [autoReplenishment, setAutoReplenishment] = useState(false);

    // State management
    const [validationResult, setValidationResult] = useState<ValidateRackResponse | null>(null);
    const [isValidating, setIsValidating] = useState(false);
    const [isCreating, setIsCreating] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [showAdvanced, setShowAdvanced] = useState(false);

    /**
     * Format rack number based on selected format
     */
    const formatRackNumber = (input: string, selectedFormat: RackFormat): string => {
        if (!input) return '';
        
        // Remove any non-alphanumeric characters
        const cleaned = input.replace(/[^a-zA-Z0-9]/g, '');
        
        switch (selectedFormat) {
            case '01': // Two digits with leading zero
                const numValue = parseInt(cleaned) || 0;
                return numValue.toString().padStart(2, '0');
            case '1': // Single digit
                return (parseInt(cleaned) || 0).toString();
            case 'A': // Single letter
                return cleaned.charAt(0).toUpperCase();
            case 'AA': // Two letters
                return cleaned.substring(0, 2).toUpperCase();
            default:
                return cleaned;
        }
    };

    /**
     * Generate example BinCode for preview
     */
    const generateExampleBinCode = (): string => {
        if (!rackNumber || verticalCount === 0 || shelfCount === 0) {
            return '';
        }

        const formattedRack = formatRackNumber(rackNumber, format);
        const vertical = '05'; // Example vertical
        const shelf = '03'; // Example shelf

        if (enableDrawers && drawerCount > 0) {
            const drawer = '01'; // Example drawer
            return `${formattedRack}${vertical}${shelf}${drawer}`;
        }

        return `${formattedRack}${vertical}${shelf}`;
    };

    /**
     * Calculate total locations that will be created
     */
    const calculateLocationCount = (): number => {
        const baseCells = verticalCount * shelfCount;
        return enableDrawers && drawerCount > 0 ? baseCells * drawerCount : baseCells;
    };

    /**
     * Validate form inputs
     */
    const validateForm = (): string | null => {
        if (!rackNumber || rackNumber.trim() === '') {
            return 'Reolnummer må være angitt';
        }

        if (verticalCount <= 0) {
            return 'Antall vertikaler må være større enn 0';
        }

        if (shelfCount <= 0) {
            return 'Antall hyller må være større enn 0';
        }

        if (verticalCount > 99) {
            return 'Antall vertikaler kan ikke overstige 99';
        }

        if (shelfCount > 99) {
            return 'Antall hyller kan ikke overstige 99';
        }

        if (enableDrawers) {
            if (drawerCount <= 0) {
                return 'Antall skuffer må være større enn 0 når 4D-reol er valgt';
            }
            if (drawerCount > 99) {
                return 'Antall skuffer kan ikke overstige 99';
            }
        }

        return null;
    };

    /**
     * Validate rack with backend (FR-008)
     */
    const validateRack = async () => {
        const validationError = validateForm();
        if (validationError) {
            setError(validationError);
            return;
        }

        setIsValidating(true);
        setError(null);
        setValidationResult(null);

        try {
            const formattedRack = formatRackNumber(rackNumber, format);
            const result = await stcBinService.validateRack({
                companyNo,
                warehouseID,
                rackNumber: formattedRack,
                verticalCount,
                shelfCount,
                stcZoneID,
                drawerCount: enableDrawers ? drawerCount : 0
            });

            setValidationResult(result);

            if (!result.isValid) {
                setError('Det finnes konflikter med eksisterende lokasjoner');
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Validering feilet');
        } finally {
            setIsValidating(false);
        }
    };

    /**
     * Create rack (FR-003)
     */
    const handleCreate = async () => {
        const validationError = validateForm();
        if (validationError) {
            setError(validationError);
            return;
        }

        // Validate first if not already validated
        if (!validationResult) {
            await validateRack();
            return;
        }

        // Check validation result
        if (validationResult && !validationResult.isValid) {
            const confirmCreate = window.confirm(
                `Det finnes ${validationResult.existingBinCodes.length} eksisterende lokasjon(er) som vil bli hoppet over.\n\n` +
                `Vil du fortsette med å opprette ${validationResult.locationsToCreate} nye lokasjoner?`
            );
            
            if (!confirmCreate) {
                return;
            }
        }

        setIsCreating(true);
        setError(null);

        try {
            const formattedRack = formatRackNumber(rackNumber, format);
            const request: CreateRackRequest = {
                companyNo,
                warehouseID,
                rackNumber: formattedRack,
                verticalCount,
                shelfCount,
                drawerCount: enableDrawers ? drawerCount : 0,
                stcZoneID,
                binSizeNo,
                binTypeNo,
                resourceNo,
                binGrpNo,
                pickPriorityNo,
                pickableYesNo,
                reservationAllowed,
                autoReplenishment
            };

            const response = await stcBinService.createRack(request);

            if (response.errors.length > 0) {
                setError(`Reol opprettet med ${response.errors.length} feil:\n${response.errors.join('\n')}`);
            } else {
                // Success - close dialog and refresh
                onSuccess();
                onClose();
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Kunne ikke opprette reol');
        } finally {
            setIsCreating(false);
        }
    };

    // Auto-validate when key fields change
    useEffect(() => {
        if (rackNumber && verticalCount > 0 && shelfCount > 0) {
            setValidationResult(null); // Reset validation when form changes
        }
    }, [rackNumber, verticalCount, shelfCount, format, enableDrawers, drawerCount]);

    const exampleBinCode = generateExampleBinCode();
    const locationCount = calculateLocationCount();
    const formIsValid = validateForm() === null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200">
                    <h2 className="text-2xl font-bold text-gray-800">Opprett Ny Reol</h2>
                    <button
                        onClick={onClose}
                        disabled={isCreating}
                        className="text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
                    >
                        <X className="w-6 h-6" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6 space-y-6">
                    {/* Error message */}
                    {error && (
                        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg flex items-start space-x-2">
                            <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                            <div className="flex-1 whitespace-pre-wrap">{error}</div>
                        </div>
                    )}

                    {/* Basic inputs */}
                    <div className="grid grid-cols-2 gap-4">
                        {/* Rack number */}
                        <div className="col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Reolnummer *
                            </label>
                            <input
                                type="text"
                                value={rackNumber}
                                onChange={(e) => setRackNumber(e.target.value)}
                                disabled={isCreating}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                                placeholder="F.eks: 01, A, CD"
                            />
                        </div>

                        {/* Format */}
                        <div className="col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Format *
                            </label>
                            <select
                                value={format}
                                onChange={(e) => setFormat(e.target.value as RackFormat)}
                                disabled={isCreating}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                            >
                                <option value="01">01 - To siffer med leading zero (01-99)</option>
                                <option value="1">1 - Enkelt siffer (1-9)</option>
                                <option value="A">A - Enkelt bokstav (A-Z)</option>
                                <option value="AA">AA - To bokstaver (AA-ZZ)</option>
                            </select>
                            {rackNumber && (
                                <p className="mt-1 text-sm text-gray-500">
                                    Formatert: <span className="font-mono font-bold">{formatRackNumber(rackNumber, format)}</span>
                                </p>
                            )}
                        </div>

                        {/* Vertical count */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Antall vertikaler *
                            </label>
                            <input
                                type="number"
                                min="1"
                                max="99"
                                value={verticalCount}
                                onChange={(e) => setVerticalCount(parseInt(e.target.value) || 0)}
                                disabled={isCreating}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                            />
                        </div>

                        {/* Shelf count */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Antall hyller *
                            </label>
                            <input
                                type="number"
                                min="1"
                                max="99"
                                value={shelfCount}
                                onChange={(e) => setShelfCount(parseInt(e.target.value) || 0)}
                                disabled={isCreating}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                            />
                        </div>

                        {/* 4D Rack (Drawer) option */}
                        <div className="col-span-2 border-t pt-4 mt-2">
                            <label className="flex items-center space-x-3 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={enableDrawers}
                                    onChange={(e) => {
                                        setEnableDrawers(e.target.checked);
                                        if (!e.target.checked) {
                                            setDrawerCount(0);
                                        } else if (drawerCount === 0) {
                                            setDrawerCount(3);
                                        }
                                    }}
                                    disabled={isCreating}
                                    className="w-5 h-5 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                                />
                                <div>
                                    <span className="text-sm font-medium text-gray-700">4D-reol (med skuffer)</span>
                                    <p className="text-xs text-gray-500">Legg til en fjerde dimensjon for lokasjoner med flere plasser</p>
                                </div>
                            </label>
                        </div>

                        {/* Drawer count (only visible when 4D enabled) */}
                        {enableDrawers && (
                            <div className="col-span-2 pl-8 bg-purple-50 p-4 rounded-lg border border-purple-200">
                                <label className="block text-sm font-medium text-purple-800 mb-2">
                                    Antall skuffer per lokasjon *
                                </label>
                                <input
                                    type="number"
                                    min="1"
                                    max="99"
                                    value={drawerCount}
                                    onChange={(e) => setDrawerCount(parseInt(e.target.value) || 0)}
                                    disabled={isCreating}
                                    className="w-32 px-3 py-2 border border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 disabled:bg-gray-100"
                                />
                                <p className="mt-2 text-xs text-purple-700">
                                    Hver celle i reolen vil få {drawerCount} skuffer (f.eks. 01, 02, 03...)
                                </p>
                            </div>
                        )}
                    </div>

                    {/* Preview section */}
                    {exampleBinCode && (
                        <div className={`border rounded-lg p-4 space-y-2 ${
                            enableDrawers ? 'bg-purple-50 border-purple-200' : 'bg-blue-50 border-blue-200'
                        }`}>
                            <div className="flex items-start space-x-2">
                                <Info className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
                                    enableDrawers ? 'text-purple-600' : 'text-blue-600'
                                }`} />
                                <div className="flex-1">
                                    <p className={`text-sm font-medium ${
                                        enableDrawers ? 'text-purple-900' : 'text-blue-900'
                                    }`}>
                                        Forhåndsvisning {enableDrawers && <span className="ml-2 px-2 py-0.5 bg-purple-200 text-purple-800 rounded text-xs">4D-reol</span>}
                                    </p>
                                    <p className={`text-sm mt-1 ${
                                        enableDrawers ? 'text-purple-800' : 'text-blue-800'
                                    }`}>
                                        Eksempel BinCode: <span className="font-mono font-bold">{exampleBinCode}</span>
                                    </p>
                                    <p className={`text-sm mt-1 ${
                                        enableDrawers ? 'text-purple-800' : 'text-blue-800'
                                    }`}>
                                        Dette vil opprette <span className="font-bold">{locationCount} lokasjoner</span>
                                        {enableDrawers && drawerCount > 0 && (
                                            <span className="text-xs ml-2">
                                                ({verticalCount} × {shelfCount} × {drawerCount} skuffer)
                                            </span>
                                        )}
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Validation result */}
                    {validationResult && (
                        <div className={`border rounded-lg p-4 ${
                            validationResult.isValid
                                ? 'bg-green-50 border-green-200'
                                : 'bg-yellow-50 border-yellow-200'
                        }`}>
                            <div className="flex items-start space-x-2">
                                {validationResult.isValid ? (
                                    <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                                ) : (
                                    <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                                )}
                                <div className="flex-1">
                                    <p className={`text-sm font-medium ${
                                        validationResult.isValid ? 'text-green-900' : 'text-yellow-900'
                                    }`}>
                                        {validationResult.isValid
                                            ? 'Alle lokasjoner er tilgjengelige'
                                            : `${validationResult.existingBinCodes.length} lokasjon(er) eksisterer allerede`
                                        }
                                    </p>
                                    <p className={`text-sm mt-1 ${
                                        validationResult.isValid ? 'text-green-800' : 'text-yellow-800'
                                    }`}>
                                        {validationResult.locationsToCreate} nye lokasjon(er) vil bli opprettet
                                    </p>
                                    {validationResult.warnings.length > 0 && (
                                        <ul className="text-sm text-yellow-800 mt-2 space-y-1">
                                            {validationResult.warnings.map((warning, idx) => (
                                                <li key={idx}>• {warning}</li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Advanced settings toggle */}
                    <div>
                        <button
                            onClick={() => setShowAdvanced(!showAdvanced)}
                            className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                        >
                            {showAdvanced ? '▼' : '▶'} Standardverdier (valgfritt)
                        </button>
                    </div>

                    {/* Advanced settings */}
                    {showAdvanced && (
                        <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                            <div className="col-span-2">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Sone ID
                                </label>
                                <input
                                    type="text"
                                    value={stcZoneID}
                                    onChange={(e) => setStcZoneID(e.target.value)}
                                    disabled={isCreating}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Bin Størrelse Nr
                                </label>
                                <input
                                    type="number"
                                    value={binSizeNo}
                                    onChange={(e) => setBinSizeNo(parseInt(e.target.value) || 1)}
                                    disabled={isCreating}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Bin Type Nr
                                </label>
                                <input
                                    type="number"
                                    value={binTypeNo}
                                    onChange={(e) => setBinTypeNo(parseInt(e.target.value) || 1)}
                                    disabled={isCreating}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Ressurs Nr
                                </label>
                                <input
                                    type="number"
                                    value={resourceNo || ''}
                                    onChange={(e) => setResourceNo(e.target.value ? parseInt(e.target.value) : undefined)}
                                    disabled={isCreating}
                                    placeholder="Valgfri"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Bin Gruppe Nr
                                </label>
                                <input
                                    type="number"
                                    value={binGrpNo || ''}
                                    onChange={(e) => setBinGrpNo(e.target.value ? parseInt(e.target.value) : undefined)}
                                    disabled={isCreating}
                                    placeholder="Valgfri"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Plukk Prioritet Nr
                                </label>
                                <input
                                    type="number"
                                    value={pickPriorityNo || ''}
                                    onChange={(e) => setPickPriorityNo(e.target.value ? parseInt(e.target.value) : undefined)}
                                    disabled={isCreating}
                                    placeholder="Valgfri"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                                />
                            </div>

                            <div className="col-span-2 space-y-2">
                                <label className="flex items-center space-x-2">
                                    <input
                                        type="checkbox"
                                        checked={pickableYesNo}
                                        onChange={(e) => setPickableYesNo(e.target.checked)}
                                        disabled={isCreating}
                                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                    />
                                    <span className="text-sm text-gray-700">Plukkbar</span>
                                </label>

                                <label className="flex items-center space-x-2">
                                    <input
                                        type="checkbox"
                                        checked={reservationAllowed}
                                        onChange={(e) => setReservationAllowed(e.target.checked)}
                                        disabled={isCreating}
                                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                    />
                                    <span className="text-sm text-gray-700">Reservasjon tillatt</span>
                                </label>

                                <label className="flex items-center space-x-2">
                                    <input
                                        type="checkbox"
                                        checked={autoReplenishment}
                                        onChange={(e) => setAutoReplenishment(e.target.checked)}
                                        disabled={isCreating}
                                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                    />
                                    <span className="text-sm text-gray-700">Automatisk påfylling</span>
                                </label>
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200 bg-gray-50">
                    <button
                        onClick={onClose}
                        disabled={isCreating}
                        className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                    >
                        Avbryt
                    </button>

                    {!validationResult && (
                        <button
                            onClick={validateRack}
                            disabled={!formIsValid || isValidating || isCreating}
                            className="px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors disabled:opacity-50 flex items-center space-x-2"
                        >
                            {isValidating && <Loader2 className="w-4 h-4 animate-spin" />}
                            <span>Valider</span>
                        </button>
                    )}

                    <button
                        onClick={handleCreate}
                        disabled={!formIsValid || isValidating || isCreating}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center space-x-2"
                    >
                        {isCreating && <Loader2 className="w-4 h-4 animate-spin" />}
                        <span>Opprett</span>
                    </button>
                </div>
            </div>
        </div>
    );
};
